import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Rocket, Trash2, X } from "lucide-react";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { useDesignModel, modelLabel } from "../../lib/useDesignModel";

// Otomatik custom liste hatti: analiz -> tasarim -> sablon -> isim ailesi ->
// kolaj -> taslak. Isler sunucuda sirayla yurur; sayfa kapansa da tamamlanir.

export interface CustomAutoJob {
  id: string; analysisId: string;
  status: "pending" | "processing" | "done" | "error";
  step: string | null; error: string | null; notes: string[];
  createdAt: string; updatedAt: string;
  title: string | null; imageUrl: string | null;
  build: { id: string; status: string; draftId: string | null; error: string | null } | null;
}

const LAST_TPL_KEY = "erc:review:last-template";

const STEP_TR: Record<string, string> = {
  analyze: "Kişiselleştirme analizi", design: "Taban tasarım", template: "Düzenlenebilir şablon",
  canva: "Canva kopyası", icons: "Seçenek ikonları", variants: "İsim tasarımları", collage: "Kolaj", prepare: "Liste hazırlığı", listing: "Liste kuyruğu",
};

async function call<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}/api/custom-autopilot${path}`, {
    method, headers: authHeaders(), body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || j.message || `İstek başarısız (${r.status})`);
  return j.data as T;
}

export function startCustomAuto(analysisIds: string[], model: string, options: {
  collageAi: boolean; withVideo: boolean; listingTemplateId?: string; collectionIds?: string[];
}) {
  return call<{ queued: number; skipped: number }>("", "POST", { analysisIds, model, options });
}

/** Isin okunur durumu: liste kuyrugu bittiyse taslak, yoksa adim */
function jobState(j: CustomAutoJob): { label: string; tone: "run" | "ok" | "err" | "wait" } {
  if (j.status === "error") return { label: `Hata · ${STEP_TR[j.step ?? ""] ?? "başlangıç"}`, tone: "err" };
  if (j.status === "pending") return { label: "Sırada", tone: "wait" };
  if (j.status === "processing") return { label: STEP_TR[j.step ?? ""] ?? "Başlıyor", tone: "run" };
  if (j.build?.status === "done") return { label: "Taslak hazır", tone: "ok" };
  if (j.build?.status === "error") return { label: "Taslak hatası", tone: "err" };
  return { label: "Mockup + taslak üretiliyor", tone: "run" };
}
const TONE: Record<string, string> = {
  run: "bg-indigo-50 text-indigo-700", ok: "bg-green-50 text-green-700", err: "bg-red-50 text-red-700", wait: "bg-gray-100 text-gray-600",
};
const active = (j: CustomAutoJob) => j.status === "pending" || j.status === "processing" || (j.status === "done" && j.build != null && j.build.status !== "done" && j.build.status !== "error");

export function useCustomAutoJobs(analysisId?: string) {
  return useQuery({
    queryKey: ["custom-autopilot", analysisId ?? "all"],
    queryFn: () => call<CustomAutoJob[]>(analysisId ? `?analysisId=${encodeURIComponent(analysisId)}` : "", "GET"),
    refetchInterval: (q) => ((q.state.data ?? []).some(active) ? 5000 : false),
  });
}

/** Tek isin satiri: durum, uyarilar, taslak baglantisi, tekrar dene */
export function CustomAutoJobRow({ j, showTitle = true }: { j: CustomAutoJob; showTitle?: boolean }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["custom-autopilot"] });
  const retryM = useMutation({ mutationFn: () => call(`/${j.id}/retry`, "POST"), onSuccess: refresh });
  const delM = useMutation({ mutationFn: () => call(`/${j.id}`, "DELETE"), onSuccess: refresh });
  const st = jobState(j);
  const err = j.status === "error" ? j.error : j.build?.status === "error" ? j.build.error : null;
  return (
    <div className="flex items-start gap-3 px-3 py-2">
      {showTitle && (j.imageUrl
        ? <img src={j.imageUrl} alt="" className="h-10 w-10 flex-shrink-0 rounded object-cover" />
        : <div className="h-10 w-10 flex-shrink-0 rounded bg-gray-100" />)}
      <div className="min-w-0 flex-1">
        {showTitle && (
          <Link to={`/analyses/${j.analysisId}`} className="block truncate text-xs font-medium text-gray-800 hover:text-indigo-600" title={j.title ?? ""}>
            {j.title || j.analysisId}
          </Link>
        )}
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${TONE[st.tone]}`}>
            {st.tone === "run" && <Loader2 className="h-3 w-3 animate-spin" />}{st.label}
          </span>
          {j.build?.draftId && (
            <Link to={`/listing-builder?id=${j.build.draftId}`} className="text-[11px] font-medium text-indigo-600 hover:underline">Taslağı aç →</Link>
          )}
        </div>
        {err && <p className="mt-0.5 text-[11px] text-red-600">{err}</p>}
        {j.notes.map((n) => <p key={n} className="mt-0.5 text-[11px] text-amber-700">{n}</p>)}
      </div>
      <div className="flex flex-shrink-0 items-center gap-1">
        {j.status === "error" && (
          <button onClick={() => retryM.mutate()} disabled={retryM.isPending} title="Kaldığı yerden devam et"
            className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-indigo-600 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${retryM.isPending ? "animate-spin" : ""}`} />
          </button>
        )}
        {j.status !== "processing" && (
          <button onClick={() => delM.mutate()} disabled={delM.isPending} title="Listeden kaldır"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

/** Analizler sayfasindaki kuyruk kutusu (is yoksa gorunmez) */
export function CustomAutoQueue() {
  const { data: jobs = [] } = useCustomAutoJobs();
  const [open, setOpen] = useState(true);
  if (jobs.length === 0) return null;
  const running = jobs.filter(active).length;
  return (
    <div className="mb-3 overflow-hidden rounded-xl border border-violet-200 bg-white shadow-sm">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 bg-violet-50 px-4 py-2 text-left">
        <Rocket className="h-4 w-4 text-violet-600" />
        <span className="text-sm font-medium text-violet-900">Otomatik custom liste</span>
        <span className="text-xs text-violet-600">{running > 0 ? `${running} iş sürüyor` : `${jobs.length} iş`}</span>
        <span className="ml-auto text-xs text-violet-500">{open ? "Gizle" : "Göster"}</span>
      </button>
      {open && <div className="max-h-80 divide-y divide-gray-100 overflow-y-auto">{jobs.map((j) => <CustomAutoJobRow key={j.id} j={j} />)}</div>}
    </div>
  );
}

/** Baslatma modali: model + kolaj/video secimi */
export function CustomAutoModal({ analysisIds, onClose, onDone, productTypeKey }: {
  analysisIds: string[]; onClose: () => void; onDone: (msg: string) => void;
  /** Analizin urun tipi: son secilen sablon bu magazada yoksa eslesen sablon secilir */
  productTypeKey?: string | null;
}) {
  const qc = useQueryClient();
  const { models, model, setModel } = useDesignModel("erc:design-model:custom");
  const [collageAi, setCollageAi] = useState(false);
  const [withVideo, setWithVideo] = useState(true);
  // Liste Denetimi'nde son secilen sablon varsayilan (ayni anahtar)
  const [templateId, setTemplateId] = useState<string>(() => { try { return localStorage.getItem(LAST_TPL_KEY) ?? ""; } catch { return ""; } });
  const [collectionId, setCollectionId] = useState("");
  const { data: templates = [] } = useQuery({
    queryKey: ["listing-templates", "custom-auto"],
    queryFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/listing-templates`, { headers: authHeaders() });
      if (!r.ok) return [];
      return ((await r.json()).data ?? []) as Array<{ id: string; name: string; mockupCollectionIds?: string[]; productTypeKey?: string | null }>;
    },
  });
  // Son secilen sablon bu magazanin listesinde yoksa (ornegin tisort sablonu):
  // urun tipi eslesen, yoksa ilk sablon
  useEffect(() => {
    if (!templates.length || templates.some((t) => t.id === templateId)) return;
    const match = templates.find((t) => productTypeKey && t.productTypeKey === productTypeKey) ?? templates[0];
    setTemplateId(match.id);
  }, [templates, templateId, productTypeKey]);
  const { data: collections = [] } = useQuery({
    queryKey: ["mockup-collections", "custom-auto"],
    queryFn: async () => {
      const r = await fetch(`${getBackendUrl()}/api/mockups/collections`, { headers: authHeaders() });
      if (!r.ok) return [];
      return ((await r.json()).data ?? []) as Array<{ id: string; name: string }>;
    },
  });
  const tpl = templates.find((t) => t.id === templateId);
  const tplCollections = (tpl?.mockupCollectionIds ?? []).map((id) => collections.find((c) => c.id === id)?.name).filter(Boolean);
  const startM = useMutation({
    mutationFn: () => startCustomAuto(analysisIds, model, {
      collageAi, withVideo,
      ...(tpl ? { listingTemplateId: tpl.id } : {}),
      ...(collectionId ? { collectionIds: [collectionId] } : {}),
    }),
    onSuccess: (r) => {
      if (tpl) { try { localStorage.setItem(LAST_TPL_KEY, tpl.id); } catch { /* depolama kapali */ } }
      void qc.invalidateQueries({ queryKey: ["custom-autopilot"] });
      onDone(`${r.queued} analiz otomatik hatta alındı${r.skipped ? ` · ${r.skipped} zaten sırada` : ""}.`);
    },
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Rocket className="h-5 w-5 text-violet-600" /> Otomatik Custom Liste
          </h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <p className="mb-2 text-sm text-gray-600">
          <b>{analysisIds.length}</b> analiz için sırayla: kişiselleştirme analizi → taban tasarım → düzenlenebilir şablon →
          isim tasarımları → kolaj → mockuplar → taslak.
        </p>
        <p className="mb-4 text-xs text-gray-500">
          Panelde şablonu hazırlanmış tasarım varsa yeniden üretilmez. Çok baskılı (ön/arka farklı) ürünler panelden yapılır.
        </p>
        <label className="mb-1 block text-xs font-medium text-gray-600">Listing şablonu</label>
        <select value={tpl ? templateId : ""} onChange={(e) => setTemplateId(e.target.value)}
          className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200">
          <option value="">Kategorinin üretim planından</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <label className="mb-1 block text-xs font-medium text-gray-600">Mockup grubu</label>
        <select value={collectionId} onChange={(e) => setCollectionId(e.target.value)}
          className="mb-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200">
          <option value="">{tplCollections.length ? `Şablonun grubu (${tplCollections.join(", ")})` : "Şablonun / üretim planının grubu"}</option>
          {collections.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {tpl && !tplCollections.length && !collectionId && (
          <p className="mb-1 text-[11px] text-amber-700">Bu şablona bağlı mockup grubu yok — bir grup seçin.</p>
        )}
        <div className="mb-4" />
        <label className="mb-1 block text-xs font-medium text-gray-600">Görsel modeli</label>
        <select value={model} onChange={(e) => setModel(e.target.value)}
          className="mb-4 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200">
          {models.map((m) => <option key={m.id} value={m.id}>{modelLabel(m)}</option>)}
        </select>
        <label className="mb-2 flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={collageAi} onChange={(e) => setCollageAi(e.target.checked)} className="mt-0.5 rounded border-gray-300 text-violet-600" />
          <span>Kolajda AI son adımı <span className="text-xs text-gray-400">(baskıları kumaşa oturtur, ek ücret; kapalıyken kodla basılır)</span></span>
        </label>
        <label className="mb-5 flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={withVideo} onChange={(e) => setWithVideo(e.target.checked)} className="mt-0.5 rounded border-gray-300 text-violet-600" />
          <span>Slayt video da üret</span>
        </label>
        {startM.isError && <p className="mb-3 text-xs text-red-600">{(startM.error as Error).message}</p>}
        <div className="flex gap-2">
          <button onClick={() => startM.mutate()} disabled={startM.isPending || !model}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
            {startM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            {startM.isPending ? "Kuyruğa alınıyor…" : "Başlat"}
          </button>
          <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50">İptal</button>
        </div>
      </div>
    </div>
  );
}
